import cx from "classnames";
import Image from "next/image";
import { HTMLProps } from "react";

import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { useSettings } from "shared/SettingsProvider/useSettings";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { Loader } from "ui/Loader";
import { formatToCurrency, priceAfterDiscount } from "utils/price.utils";
import { usePriceAdvantage } from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import { ProductOrderButton } from "./ProductOrderButton";
import TrendDown from "./trend-down.svg";

type StickySectionProps = {
  className?: HTMLProps<HTMLDivElement>["className"];
  handleCreateNewOrder: () => void;
  handleWarnMe: () => void;
  isCreatingOrder: boolean;
  subscriptionSku: string | null | undefined;
};

export const StickySection = ({
  className,
  handleCreateNewOrder,
  handleWarnMe,
  isCreatingOrder,
  subscriptionSku,
}: StickySectionProps) => {
  const i18n = useI18n();
  const { currentVariant } = useProductPageContext();
  const { isSwap } = useFunnelContext();
  const { isBlackFriday } = useSettings();

  const isBlackWeekProduct =
    currentVariant?.tagSlugs?.includes("black-week") ?? false;
  const isBlackFridayTheme = isBlackFriday && isBlackWeekProduct;

  const priceAdvantage = usePriceAdvantage({
    originalUpfrontPrice: currentVariant?.originalUpfrontPrice,
    upfrontPrice: currentVariant?.upfrontPrice,
    originalSubscriptionPrice: currentVariant?.originalSubscriptionPrice,
    subscriptionPrice: currentVariant?.subscriptionPrice,
  });

  const {
    discountInfo,
    smartphoneFamilyHasSubscriptionPriceDrop,
    hasUpfrontPriceDrop,
    formatedOriginalPriceUpfront,
    discountPercentage,
    currentSeniorityPlan,
    hasSeniorityDiscount,
  } = priceAdvantage;

  if (isSwap && !discountInfo) {
    return <Loader />;
  }

  const hasPriceDrop =
    hasUpfrontPriceDrop || smartphoneFamilyHasSubscriptionPriceDrop;

  const upfrontPriceLabel =
    hasUpfrontPriceDrop || currentSeniorityPlan
      ? i18n.t(
          "product.available.stickyUpfrontDiscount",
          {
            price: formatToCurrency(
              "fr-FR",
              "EUR",
              priceAfterDiscount(
                currentVariant?.upfrontPrice ?? 0,
                discountPercentage ?? 0,
              ),
            ).replace(",00", ""),
            priceClassName: "font-semibold text-red-discount",
          },
          { html: true },
        )
      : i18n.t(
          "product.available.stickyUpfront",
          { price: formatedOriginalPriceUpfront, className: "font-semibold" },
          { html: true },
        );

  return (
    <div
      className={`${className} w-full bg-lotion z-20 shadow-[0_-4px_20px_0px_rgba(0,0,0,0.15)]`}
    >
      {/* Promotion alert */}
      {hasSeniorityDiscount && (
        <div className="flex gap-2 justify-center items-center py-0.5 bg-gradient-to-r from-neon-blue to-sky-magenta">
          <p className="text-white">
            {i18n.t(
              "catalog.badge.productFreeUpfront",
              {},
              {
                html: true,
              },
            )}
          </p>
        </div>
      )}

      {!hasSeniorityDiscount && (isBlackFridayTheme || hasPriceDrop) && (
        <div
          className={cx(
            "flex gap-2 justify-center items-center py-0.5 font-bold",
            isBlackFridayTheme ? "bg-christmas" : "bg-red-discount-light",
          )}
        >
          {isBlackFridayTheme && (
            <Image src="/images/christmas/gift.svg" width={16} height={16} />
          )}
          <p
            className={
              isBlackFridayTheme
                ? "text-transparent bg-clip-text bg-white"
                : "text-red-discount"
            }
          >
            {i18n.t(
              isBlackFridayTheme
                ? "product.available.blackFridayStickyUpfrontTag"
                : "product.available.stickyUpfrontTag",
            )}
          </p>
          {!isBlackFridayTheme && <TrendDown />}
        </div>
      )}

      <div className="flex flex-col gap-2 justify-between items-center py-4 px-5 mx-auto max-w-md 2xs:flex-row">
        {/* Price infos */}
        <div className="flex flex-row justify-between items-end w-full text-sm 2xs:flex-col 2xs:items-start 2xs:w-max 2xs:text-base">
          <h2>
            {smartphoneFamilyHasSubscriptionPriceDrop ? (
              <span className="text-2xl font-bold text-red-discount bg-yellow">
                {formatToCurrency(
                  "fr-FR",
                  "EUR",
                  currentVariant?.subscriptionPrice ?? 0,
                )}
              </span>
            ) : (
              <span className="text-2xl font-bold text-black bg-yellow">
                {formatToCurrency(
                  "fr-FR",
                  "EUR",
                  currentVariant?.subscriptionPrice ?? 0,
                )}
              </span>
            )}

            <span className="ml-2">
              {i18n.t("product.available.stickySubscriptionPrice")}
            </span>
          </h2>
          <p className="mb-[4px]">{upfrontPriceLabel}</p>
        </div>

        {/* Order button */}
        <ProductOrderButton
          className="flex-none w-full 2xs:w-max"
          handleCreateNewOrder={handleCreateNewOrder}
          handleWarnMe={handleWarnMe}
          isCreatingOrder={isCreatingOrder}
          subscriptionSku={subscriptionSku}
        />
      </div>
    </div>
  );
};
